import React, { useRef, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  Chip,
  Grid,
  Divider,
  alpha,
  useTheme,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  RotateLeft as RotateLeftIcon,
  RotateRight as RotateRightIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  PanTool as PanToolIcon,
  OpenWith as OpenWithIcon,
  RestartAlt as RestartAltIcon,
} from '@mui/icons-material';
import { motion, useMotionValue, PanInfo } from 'framer-motion';
import { ParsedPrescriptionData } from '../../types/prescription.types';

interface PrescriptionPreviewProps {
  imageUrl: string;
  onClose: () => void;
  onDownload: () => void;
  extractedData?: ParsedPrescriptionData | null;
}

export const PrescriptionPreview: React.FC<PrescriptionPreviewProps> = ({
  imageUrl,
  onClose,
  onDownload,
  extractedData,
}) => {
  const theme = useTheme();
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [showExtractedInfo, setShowExtractedInfo] = useState(true);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [_position, setPosition] = useState({ x: 0, y: 0 });
  const [isPanMode, setIsPanMode] = useState(false);
  
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  
  // Motion values for panning
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.25, 3));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.25, 0.5));
  const handleRotateLeft = () => setRotation(prev => prev - 90);
  const handleRotateRight = () => setRotation(prev => prev + 90);
  
  const handleReset = () => {
    setZoom(1);
    setRotation(0);
    setPosition({ x: 0, y: 0 });
    x.set(0);
    y.set(0);
  };

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    setImageSize({
      width: img.naturalWidth,
      height: img.naturalHeight,
    });
  };

  const handlePanStart = () => {
    setIsDragging(true);
  };

  const handlePanEnd = () => {
    setIsDragging(false);
  };

  const handlePan = (_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    if (zoom > 1) {
      // Only allow panning when zoomed in
      const newX = x.get() + info.delta.x;
      const newY = y.get() + info.delta.y;
      
      // Add boundaries to prevent panning too far
      const maxPan = 100 * (zoom - 1);
      const boundedX = Math.max(Math.min(newX, maxPan), -maxPan);
      const boundedY = Math.max(Math.min(newY, maxPan), -maxPan);
      
      x.set(boundedX);
      y.set(boundedY);
      setPosition({ x: boundedX, y: boundedY });
    }
  };

  const togglePanMode = () => {
    setIsPanMode(!isPanMode);
  };

  const getConfidenceColor = (confidence?: number) => {
    if (!confidence) return 'default';
    if (confidence >= 0.85) return 'success';
    if (confidence >= 0.6) return 'warning';
    return 'error';
  };

  const getConfidenceIcon = (confidence?: number) => {
    if (!confidence) return <InfoIcon />;
    if (confidence >= 0.85) return <CheckIcon />;
    if (confidence >= 0.6) return <InfoIcon />;
    return <ErrorIcon />;
  };

  return (
    <Paper
      elevation={3}
      sx={{
        position: 'relative',
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: 'background.paper',
        height: '100%',
        minHeight: 600,
        maxHeight: 800,
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: alpha(theme.palette.primary.main, 0.02),
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Prescription Preview
          </Typography>
          {imageSize.width > 0 && (
            <Chip
              size="small"
              label={`${imageSize.width} x ${imageSize.height}`}
              variant="outlined"
            />
          )}
        </Box>
        
        <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
          {/* Pan Mode Toggle */}
          <Tooltip title={isPanMode ? "Disable Pan Mode" : "Enable Pan Mode (Hand Tool)"}>
            <IconButton
              size="small"
              onClick={togglePanMode}
              color={isPanMode ? 'primary' : 'default'}
              sx={{
                bgcolor: isPanMode ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
              }}
            >
              <PanToolIcon />
            </IconButton>
          </Tooltip>

          <IconButton size="small" onClick={handleZoomOut} title="Zoom Out">
            <ZoomOutIcon />
          </IconButton>
          
          <Typography variant="body2" sx={{ minWidth: 60, textAlign: 'center' }}>
            {Math.round(zoom * 100)}%
          </Typography>
          
          <IconButton size="small" onClick={handleZoomIn} title="Zoom In">
            <ZoomInIcon />
          </IconButton>
          
          <IconButton size="small" onClick={handleRotateLeft} title="Rotate Left">
            <RotateLeftIcon />
          </IconButton>
          
          <IconButton size="small" onClick={handleRotateRight} title="Rotate Right">
            <RotateRightIcon />
          </IconButton>
          
          <IconButton size="small" onClick={handleReset} title="Reset">
            <RestartAltIcon />
          </IconButton>
          
          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
          
          <IconButton
            size="small"
            onClick={() => setShowExtractedInfo(!showExtractedInfo)}
            color={showExtractedInfo ? 'primary' : 'default'}
            title="Toggle Extracted Info"
          >
            <InfoIcon />
          </IconButton>
          
          <Button
            size="small"
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={onDownload}
            sx={{ ml: 1 }}
          >
            Download
          </Button>
          
          <IconButton size="small" onClick={onClose} color="error">
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Image Container - With Hand Cursor when in pan mode */}
        <Box
          ref={containerRef}
          sx={{
            flex: showExtractedInfo ? 1.5 : 1,
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: '#f5f5f5',
            position: 'relative',
            transition: 'flex 0.3s ease',
            cursor: isPanMode ? (isDragging ? 'grabbing' : 'grab') : 'default',
          }}
          onMouseDown={() => isPanMode && setIsDragging(true)}
          onMouseUp={() => isPanMode && setIsDragging(false)}
          onMouseLeave={() => isPanMode && setIsDragging(false)}
        >
          <motion.div
            drag={isPanMode && zoom > 1}
            dragMomentum={false}
            dragElastic={0}
            onDragStart={handlePanStart}
            onDragEnd={handlePanEnd}
            onDrag={handlePan}
            style={{
              x: isPanMode ? x : 0,
              y: isPanMode ? y : 0,
              display: 'inline-block',
              cursor: isPanMode ? (isDragging ? 'grabbing' : 'grab') : 'default',
            }}
            animate={{
              scale: zoom,
              rotate: rotation,
            }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          >
            <img
              ref={imageRef}
              src={imageUrl}
              alt="Prescription"
              onLoad={handleImageLoad}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                display: 'block',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                borderRadius: 4,
                pointerEvents: isPanMode ? 'none' : 'auto',
              }}
              draggable={false}
            />
          </motion.div>

          {/* Pan Mode Indicator */}
          {isPanMode && zoom > 1 && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 10,
                right: 10,
                bgcolor: 'rgba(0,0,0,0.6)',
                color: 'white',
                px: 1,
                py: 0.5,
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <OpenWithIcon fontSize="small" />
              <Typography variant="caption">Drag to pan</Typography>
            </Box>
          )}
        </Box>

        {/* Extracted Info Panel */}
        {showExtractedInfo && (
          <motion.div
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: 400, opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            style={{
              overflow: 'hidden',
              borderLeft: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Box
              sx={{
                width: 400,
                height: '100%',
                overflow: 'auto',
                bgcolor: alpha(theme.palette.primary.main, 0.01),
              }}
            >
              <Box sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Extracted Information
                </Typography>

                {extractedData ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* Patient Name */}
                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                      <Typography variant="caption" color="textSecondary">
                        Patient Name
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {extractedData.patientName || 'Not detected'}
                        </Typography>
                        {extractedData.confidence && (
                          <Chip
                            size="small"
                            icon={getConfidenceIcon(extractedData.confidence)}
                            label={`${Math.round(extractedData.confidence * 100)}%`}
                            color={getConfidenceColor(extractedData.confidence)}
                            sx={{ height: 20 }}
                          />
                        )}
                      </Box>
                    </Paper>

                    {/* Mobile Number */}
                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                      <Typography variant="caption" color="textSecondary">
                        Mobile Number
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {extractedData.mobileNumber || 'Not detected'}
                      </Typography>
                    </Paper>

                    {/* Age & Gender */}
                    <Grid container spacing={1}>
                      <Grid size={{ xs: 6 }}>
                        <Paper variant="outlined" sx={{ p: 1.5 }}>
                          <Typography variant="caption" color="textSecondary">
                            Age
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {extractedData.age || 'Not detected'}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Paper variant="outlined" sx={{ p: 1.5 }}>
                          <Typography variant="caption" color="textSecondary">
                            Gender
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {extractedData.gender || 'Not detected'}
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>

                    {/* Doctor Name */}
                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                      <Typography variant="caption" color="textSecondary">
                        Referring Doctor
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {extractedData.doctorName || 'Not detected'}
                      </Typography>
                    </Paper>

                    {/* Date */}
                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                      <Typography variant="caption" color="textSecondary">
                        Prescription Date
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {extractedData.date || 'Not detected'}
                      </Typography>
                    </Paper>

                    {/* Tests */}
                    {extractedData.tests && extractedData.tests.length > 0 && (
                      <Paper variant="outlined" sx={{ p: 1.5 }}>
                        <Typography variant="caption" color="textSecondary" gutterBottom>
                          Detected Tests ({extractedData.tests.length})
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                          {extractedData.tests.map((test, idx) => (
                            <Chip
                              key={idx}
                              label={test.name}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ fontSize: '0.7rem' }}
                            />
                          ))}
                        </Box>
                      </Paper>
                    )}

                    {/* Overall Confidence */}
                    {extractedData.confidence && (
                      <Box sx={{ mt: 2 }}>
                        <Divider sx={{ mb: 2 }} />
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Typography variant="body2">Overall Confidence</Typography>
                          <Chip
                            size="small"
                            icon={getConfidenceIcon(extractedData.confidence)}
                            label={`${Math.round(extractedData.confidence * 100)}%`}
                            color={getConfidenceColor(extractedData.confidence)}
                          />
                        </Box>
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <InfoIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography color="textSecondary">
                      No data extracted yet. Process the prescription to see extracted information.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </motion.div>
        )}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          p: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: alpha(theme.palette.primary.main, 0.02),
        }}
      >
        <Typography variant="caption" color="textSecondary">
          {extractedData ? 'AI-processed prescription' : 'Original prescription image'}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          Zoom: {Math.round(zoom * 100)}% | Rotation: {rotation}°
        </Typography>
      </Box>
    </Paper>
  );
};



// // src/components/PrescriptionPreview.tsx
// import React, { useState } from 'react';
// import {
//   Box,
//   Typography,
//   IconButton,
//   Chip,
//   Grid,
//   Card,
//   CardMedia,
//   Slider,
// } from '@mui/material';
// import {
//   Close as CloseIcon,
//   ZoomIn as ZoomInIcon,
//   ZoomOut as ZoomOutIcon,
//   Download as DownloadIcon,
//   Phone as PhoneIcon,
//   FitScreen as FitScreenIcon,
// } from '@mui/icons-material';
// import { styled } from '@mui/material/styles';

// const PreviewCard = styled(Card)(({ theme }) => ({
//   background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
//   borderRadius: '16px',
//   border: '1px solid rgba(255,215,0,0.2)',
//   position: 'relative',
//   height: '100%',
//   display: 'flex',
//   flexDirection: 'column',
// }));

// const ImageContainer = styled(Box)(({ theme }) => ({
//   flex: 1,
//   overflow: 'auto',
//   bgcolor: '#0a0a1a',
//   borderRadius: '16px 16px 0 0',
//   position: 'relative',
//   minHeight: 0, // Important for flex child to scroll properly
//   '&::-webkit-scrollbar': {
//     width: '8px',
//     height: '8px',
//   },
//   '&::-webkit-scrollbar-track': {
//     background: 'rgba(255,255,255,0.05)',
//     borderRadius: '4px',
//   },
//   '&::-webkit-scrollbar-thumb': {
//     background: 'linear-gradient(135deg, #667eea, #764ba2)',
//     borderRadius: '4px',
//     '&:hover': {
//       background: 'linear-gradient(135deg, #764ba2, #667eea)',
//     },
//   },
// }));

// interface PrescriptionPreviewProps {
//   imageUrl: string;
//   onClose?: () => void;
//   onDownload?: () => void;
//   extractedData?: any;
// }

// export const PrescriptionPreview: React.FC<PrescriptionPreviewProps> = ({
//   imageUrl,
//   onClose,
//   onDownload,
//   extractedData,
// }) => {
//   const [zoomLevel, setZoomLevel] = useState(1);
//   const [fitToScreen, setFitToScreen] = useState(false);
//   const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });

//   const handleZoomIn = () => {
//     setZoomLevel(prev => Math.min(prev + 0.25, 3));
//     setFitToScreen(false);
//   };

//   const handleZoomOut = () => {
//     setZoomLevel(prev => Math.max(prev - 0.25, 0.5));
//     setFitToScreen(false);
//   };

//   const handleFitToScreen = () => {
//     setFitToScreen(true);
//     setZoomLevel(1);
//   };

//   const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
//     const img = event.currentTarget;
//     setImageDimensions({
//       width: img.naturalWidth,
//       height: img.naturalHeight,
//     });
//   };

//   return (
//     <PreviewCard>
//       <Box sx={{ position: 'relative', height: '100%' , minHeight:600, display: 'flex', flexDirection: 'column' }}>
//         {/* Header with controls */}
//         <Box
//           sx={{
//             position: 'absolute',
//             top: 8,
//             right: 8,
//             zIndex: 2,
//             display: 'flex',
//             gap: 1,
//             bgcolor: 'rgba(0,0,0,0.6)',
//             backdropFilter: 'blur(10px)',
//             borderRadius: '20px',
//             p: 0.5,
//           }}
//         >
//           <IconButton
//             size="small"
//             onClick={handleZoomOut}
//             disabled={zoomLevel <= 0.5}
//             sx={{ 
//               color: 'white',
//               '&:hover': { bgcolor: 'rgba(255,215,0,0.2)' },
//               '&.Mui-disabled': { opacity: 0.3 },
//             }}
//           >
//             <ZoomOutIcon fontSize="small" />
//           </IconButton>
          
//           <Box sx={{ display: 'flex', alignItems: 'center', px: 1 }}>
//             <Typography variant="caption" sx={{ color: 'white', fontWeight: 600 }}>
//               {Math.round(zoomLevel * 100)}%
//             </Typography>
//           </Box>
          
//           <IconButton
//             size="small"
//             onClick={handleZoomIn}
//             disabled={zoomLevel >= 3}
//             sx={{ 
//               color: 'white',
//               '&:hover': { bgcolor: 'rgba(255,215,0,0.2)' },
//               '&.Mui-disabled': { opacity: 0.3 },
//             }}
//           >
//             <ZoomInIcon fontSize="small" />
//           </IconButton>
          
//           <IconButton
//             size="small"
//             onClick={handleFitToScreen}
//             sx={{ 
//               color: fitToScreen ? '#FFD700' : 'white',
//               '&:hover': { bgcolor: 'rgba(255,215,0,0.2)' },
//             }}
//           >
//             <FitScreenIcon fontSize="small" />
//           </IconButton>
          
//           <IconButton
//             size="small"
//             onClick={onDownload}
//             sx={{ 
//               color: 'white',
//               '&:hover': { bgcolor: 'rgba(255,215,0,0.2)' },
//             }}
//           >
//             <DownloadIcon fontSize="small" />
//           </IconButton>
          
//           {onClose && (
//             <IconButton
//               size="small"
//               onClick={onClose}
//               sx={{ 
//                 color: 'white',
//                 '&:hover': { bgcolor: 'rgba(255,0,0,0.3)' },
//               }}
//             >
//               <CloseIcon fontSize="small" />
//             </IconButton>
//           )}
//         </Box>

//         {/* Zoom Slider */}
//         <Box
//           sx={{
//             position: 'absolute',
//             bottom: extractedData ? 120 : 16,
//             left: '50%',
//             transform: 'translateX(-50%)',
//             zIndex: 2,
//             width: 200,
//             bgcolor: 'rgba(0,0,0,0.6)',
//             backdropFilter: 'blur(10px)',
//             borderRadius: '20px',
//             p: 1,
//           }}
//         >
//           <Slider
//             value={zoomLevel}
//             min={0.5}
//             max={3}
//             step={0.1}
//             onChange={(_, value) => {
//               setZoomLevel(value as number);
//               setFitToScreen(false);
//             }}
//             sx={{
//               color: '#FFD700',
//               '& .MuiSlider-thumb': {
//                 width: 16,
//                 height: 16,
//               },
//             }}
//           />
//         </Box>

//         {/* Image Container */}
//         <ImageContainer>
//           <CardMedia
//             component="img"
//             image={imageUrl}
//             alt="Prescription"
//             onLoad={handleImageLoad}
//             sx={{
//               transform: fitToScreen ? 'scale(1)' : `scale(${zoomLevel})`,
//               transition: 'transform 0.3s ease',
//               transformOrigin: 'top left',
//               width: fitToScreen ? '100%' : 'auto',
//               height: fitToScreen ? 'auto' : 'auto',
//               maxWidth: fitToScreen ? '100%' : 'none',
//               maxHeight: fitToScreen ? '100%' : 'none',
//               objectFit: fitToScreen ? 'contain' : 'none',
//             }}
//           />
//         </ImageContainer>

//         {/* Image Dimensions Info */}
//         {imageDimensions.width > 0 && (
//           <Box
//             sx={{
//               position: 'absolute',
//               top: 60,
//               left: 16,
//               zIndex: 2,
//               bgcolor: 'rgba(0,0,0,0.6)',
//               backdropFilter: 'blur(10px)',
//               borderRadius: '12px',
//               p: 1,
//             }}
//           >
//             <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
//               {imageDimensions.width} x {imageDimensions.height} px
//             </Typography>
//           </Box>
//         )}

//         {/* Extracted Info Overlay */}
//         {extractedData && (
//           <Box
//             sx={{
//               p: 2,
//               background: 'linear-gradient(135deg, rgba(102,126,234,0.95), rgba(118,75,162,0.95))',
//               backdropFilter: 'blur(10px)',
//               borderTop: '1px solid rgba(255,215,0,0.3)',
//             }}
//           >
//             <Grid container spacing={2}>
//               <Grid size={{ xs:6 }}>
//                 <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
//                   Patient
//                 </Typography>
//                 <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>
//                   {extractedData.patientName || 'Not detected'}
//                 </Typography>
//               </Grid>
//               <Grid size={{ xs:3 }} >
//                 <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
//                   Age/Gender
//                 </Typography>
//                 <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>
//                   {extractedData.age || '?'}/{extractedData.gender || '?'}
//                 </Typography>
//               </Grid>
//               <Grid size={{ xs:3 }}>
//                 <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
//                   Date
//                 </Typography>
//                 <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>
//                   {extractedData.date || 'Not detected'}
//                 </Typography>
//               </Grid>
//             </Grid>

//             {extractedData.mobileNumber && (
//               <Chip
//                 icon={<PhoneIcon sx={{ fontSize: 16 }} />}
//                 label={`Mobile: ${extractedData.mobileNumber}`}
//                 size="small"
//                 sx={{ 
//                   mt: 1, 
//                   bgcolor: 'rgba(255,215,0,0.2)', 
//                   color: '#FFD700',
//                   '& .MuiChip-icon': { color: '#FFD700' },
//                 }}
//               />
//             )}
//           </Box>
//         )}
//       </Box>
//     </PreviewCard>
//   );
// };